import React, { useState } from "react";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";

export default function PropertyValueInput({ label, value = {}, onChange }) {
  // 1. Initialize local state from the passed object.
  // We generate a unique ID for each row so inputs don't lose focus when editing keys.
  const [items, setItems] = useState(() =>
    Object.entries(value).map(([key, val]) => ({
      id: Math.random().toString(36).substring(2, 9),
      key,
      value: val,
    })),
  );

  // 2. Build the JS Object and send it to the parent
  const triggerChange = (newItems) => {
    const newObj = {};
    newItems.forEach((item) => {
      // Only add to object if the property name isn't empty
      if (item.key.trim() !== "") {
        newObj[item.key.trim()] = item.value;
      }
    });
    onChange(newObj);
  };

  const handleAdd = () => {
    setItems([
      ...items,
      { id: Math.random().toString(36).substring(2, 9), key: "", value: "" },
    ]);
  };

  const handleRemove = (id) => {
    const newItems = items.filter((item) => item.id !== id);
    setItems(newItems);
    triggerChange(newItems);
  };

  const handleChange = (id, field, newValue) => {
    const newItems = items.map((item) =>
      item.id === id ? { ...item, [field]: newValue } : item,
    );
    setItems(newItems);
    triggerChange(newItems);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px",
        px: 2,
        py: 2,
      }}
    >
      {label && (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ fontWeight: "450", fontSize: "14px", mt: -0.5 }}
        >
          {label}
        </Typography>
      )}

      {items.map((item) => (
        <Box
          key={item.id}
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
        >
          <TextField
            label="Property"
            value={item.key}
            onChange={(e) => handleChange(item.id, "key", e.target.value)}
            fullWidth
          />
          <TextField
            label="Value"
            value={item.value}
            onChange={(e) => handleChange(item.id, "value", e.target.value)}
            fullWidth
          />
          <IconButton
            onClick={() => handleRemove(item.id)}
            size="small"
            aria-label="Delete property"
          >
            <Delete />
          </IconButton>
        </Box>
      ))}

      <Button
        startIcon={<Add />}
        onClick={handleAdd}
        size="small"
        sx={{ alignSelf: "flex-start", textTransform: "none" }}
      >
        Add property
      </Button>
    </Box>
  );
}
